import {
    convertMarkdownToStringifiedEditorState,
    convertStringifiedEditorStateToMarkdown,
} from 'magic-text-box-lexical/src/utils/markdown'
import { StringifiedMagicTextBox } from 'magic-text-box-lexical/src/components/types'

// The markdown converter emits nodes (annotatable-text-node, headings, code)
// that comment editors don't register — the outline annotation panel's
// read-only comment editor CRASHES on an unregistered node type, and the
// editable draft editor below would refuse to load the state. Rewrite the
// unsupported ones to the plain equivalents the comment editors DO handle
// (text/paragraph), so the draft is shaped like a native comment.
// Formatting (bold/italic via the text `format` flag), lists, quotes, links,
// and math (EquationNode) are all supported and pass through untouched.
const NODE_TYPE_DOWNGRADES: Record<string, string> = {
    'annotatable-text-node': 'text',
    customlistitem: 'listitem',
    'code-highlight': 'text',
    heading: 'paragraph',
    code: 'paragraph',
    horizontalrule: 'paragraph',
}

// Lexical's IS_CODE text-format bit. Code-formatted text renders as a pale
// box the comment surfaces don't restyle for their dark background, so a
// backticked spreadsheet formula was unreadable. A formula reads fine as
// plain text, so the bit is dropped.
const IS_CODE_FORMAT = 1 << 4

// Inline code spans are lifted out before conversion: the importer's
// currency/LaTeX passes would otherwise run over their contents, turning
// `SUMPRODUCT(..., $G$36:$G$42)` into a code fragment, an equation and
// another fragment. The literal text goes back in afterwards.
const codeSpanToken = (index: number): string => `MTBCODESPAN${index}X`
const CODE_SPAN_TOKEN_PATTERN = /MTBCODESPAN(\d+)X/g
const liftCodeSpans = (
    markdown: string
): { markdown: string; spans: string[] } => {
    const spans: string[] = []
    const lifted = markdown.replace(/`([^`\n]+)`/g, (_match, code: string) => {
        spans.push(code)
        return codeSpanToken(spans.length - 1)
    })
    return { markdown: lifted, spans }
}
const restoreCodeSpans = (text: string, spans: string[]): string =>
    text.replace(
        CODE_SPAN_TOKEN_PATTERN,
        (match, index: string) => spans[Number(index)] ?? match
    )

const toPlainCommentEditorState = (
    serialized: string,
    codeSpans: string[]
): string => {
    try {
        const parsed = JSON.parse(serialized) as {
            root?: Record<string, unknown>
        }
        const walk = (node: Record<string, unknown>): void => {
            if (!node || typeof node !== 'object') return
            const downgrade = NODE_TYPE_DOWNGRADES[node.type as string]
            if (downgrade) node.type = downgrade
            if (typeof node.format === 'number')
                node.format = node.format & ~IS_CODE_FORMAT
            if (typeof node.text === 'string')
                node.text = restoreCodeSpans(node.text, codeSpans)
            if (typeof node.equation === 'string')
                node.equation = restoreCodeSpans(node.equation, codeSpans)
            const children = node.children
            if (Array.isArray(children))
                children.forEach((c) => walk(c as Record<string, unknown>))
        }
        if (parsed.root) walk(parsed.root)
        return JSON.stringify(parsed)
    } catch {
        return serialized
    }
}

// The markdown importer drops blank lines, so paragraph spacing written by
// the drafter collapses to nothing in the editor and the posted reply.
// Convert blank-line-separated chunks independently and stitch them together
// with real empty paragraph nodes — the same shape a hand-written comment
// gets when the author presses Enter twice.
const EMPTY_PARAGRAPH: Record<string, unknown> = {
    children: [] as unknown[],
    direction: null,
    format: '',
    indent: 0,
    type: 'paragraph',
    version: 1,
}
const splitOnBlankLines = (markdown: string): string[] => {
    const chunks: string[][] = [[]]
    let inCodeFence = false
    for (const line of markdown.split('\n')) {
        if (/^\s*```/.test(line)) inCodeFence = !inCodeFence
        if (!inCodeFence && /^\s*$/.test(line)) {
            if (chunks[chunks.length - 1].length > 0) chunks.push([])
        } else {
            chunks[chunks.length - 1].push(line)
        }
    }
    return chunks
        .filter((chunk) => chunk.length > 0)
        .map((chunk) => chunk.join('\n'))
}
const draftMarkdownToEditorState = (
    markdown: string
): StringifiedMagicTextBox => {
    const fallback = (): StringifiedMagicTextBox =>
        convertMarkdownToStringifiedEditorState(markdown)
    const chunks = splitOnBlankLines(markdown)
    if (chunks.length <= 1) return fallback()
    try {
        const parsedDocs = chunks
            .map((chunk) => convertMarkdownToStringifiedEditorState(chunk))
            .filter((serialized): serialized is string => !!serialized)
            .map(
                (serialized) =>
                    JSON.parse(serialized) as {
                        root: { children: unknown[] }
                    }
            )
        if (!parsedDocs.length) return fallback()
        const children: unknown[] = []
        parsedDocs.forEach((doc, index) => {
            if (index > 0) children.push({ ...EMPTY_PARAGRAPH })
            children.push(...doc.root.children)
        })
        const merged = parsedDocs[0]
        merged.root.children = children
        return JSON.stringify(merged)
    } catch {
        return fallback()
    }
}

// Draft markdown → a stringified editor state the comment editor can load
// and the thread can post as-is.
export const draftMarkdownToCommentEditorState = (markdown: string): string => {
    const lifted = liftCodeSpans(markdown)
    const converted = draftMarkdownToEditorState(lifted.markdown)
    return converted ? toPlainCommentEditorState(converted, lifted.spans) : ''
}

// The learning signal (draft vs what was actually posted) is kept as
// markdown, so the edited editor state is exported back. Both sides of the
// comparison go through the same export, so importer normalization doesn't
// register as an edit.
export const editorStateToMarkdown = (serialized: string): string => {
    try {
        // The exporter renders each spacer paragraph as extra newlines;
        // collapse them so the stored text reads like the draft did.
        return convertStringifiedEditorStateToMarkdown(serialized)
            .replace(/\n{3,}/g, '\n\n')
            .trim()
    } catch {
        return ''
    }
}
